import {
  DAY_IN_WEEK,
  START_DAY_WORK,
  TIME_FORMAT,
  TIMEZONE,
} from '../../constants';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { TSchedule } from 'types';
import { formatDate, getBusinessDate, getCurrentWeeks } from 'utils';
import {
  EScheduleCellKind,
  resolveScheduleCell,
} from './resolveScheduleCell';

type Props = {
  schedules: TSchedule[];
  area?: { id: number; name: string };
  isToBeConfirmed?: boolean;
  enableReadyToGo?: boolean;
};

const Spacing = () => {
  return <>&nbsp;</>;
};

const ScheduleItem = ({
  text = '-',
  kind,
}: {
  text?: string;
  kind: EScheduleCellKind;
}) => {
  if (kind === EScheduleCellKind.TIME) {
    return <>{text}</>;
  }

  return (
    <>
      <Spacing />
      <br />
      {text}
      <br />
      <Spacing />
    </>
  );
};

function ListSchedule({
  schedules,
  area,
  isToBeConfirmed,
  enableReadyToGo,
}: Props) {
  const [groupSchedule, setGroupSchedule] = useState<TSchedule[]>([]);

  useEffect(() => {
    const today = moment().tz(TIMEZONE);

    const baseWeek = getCurrentWeeks({
      currentDate: today,
      range: DAY_IN_WEEK,
    });

    const scheduleMap = new Map<string, TSchedule>();

    schedules?.forEach((schedule) => {
      const key = moment(schedule.startDate).tz(TIMEZONE).format('YYYY-MM-DD');
      scheduleMap.set(key, schedule);
    });

    const mergedSchedule = baseWeek.map((day) => {
      const key = moment(day.startDate).tz(TIMEZONE).format('YYYY-MM-DD');
      return scheduleMap.get(key) || day;
    });

    setGroupSchedule(mergedSchedule);
  }, [schedules]);

  return (
    <div className="grid grid-cols-7 gap-x-2 mt-6">
      {groupSchedule?.map((schedule, index) => {
        const {
          startDate,
          startTime,
          endTime,
          disabled,
          startDateFormat,
          activityArea: shiftArea,
        } = schedule;
        const isShowGo = () => {
          const currentTime = moment().tz(TIMEZONE);
          const selectedDate = moment(startDate)
            .tz(TIMEZONE)
            .format('YYYY-MM-DD');

          if (!enableReadyToGo || selectedDate !== getBusinessDate(currentTime))
            return false;

          if (!startTime) return enableReadyToGo;

          const startShift = moment.tz(
            `${selectedDate} ${startTime}`,
            'YYYY-MM-DD HH:mm',
            TIMEZONE,
          );

          if (
            startShift.isBefore(
              moment.tz(START_DAY_WORK, TIME_FORMAT, TIMEZONE),
            )
          ) {
            startShift.add(1, 'day');
          }

          return currentTime.isBefore(startShift);
        };

        const showReadyToGo = isShowGo();
        const { kind, timeText, areaText } = resolveScheduleCell({
          disabled,
          startTime,
          endTime,
          shiftAreaName: shiftArea?.name,
          profileAreaName: area?.name,
          showReadyToGo,
          isToBeConfirmed: !!isToBeConfirmed,
          isFirstCell: index === 0,
        });

        return (
          <div
            key={index}
            className={`flex flex-col border border-solid border-neutral-300 rounded-xl font-medium text-neutral text-center`}
          >
            <div className="py-2 flex flex-col items-center gap-1">
              <span>
                {startDateFormat || formatDate(startDate, 'MM/DD\n (dd)')}
              </span>
            </div>
            <div
              className={`border-y border-solid border-neutral-300 py-2 flex-1 flex justify-center items-center
                ${
                  schedules.some((schedule) => !schedule.disabled) &&
                  'whitespace-pre-line'
                }
              `}
            >
              <ScheduleItem text={timeText} kind={kind} />
            </div>
            <div className={`py-2`}>{areaText}</div>
          </div>
        );
      })}
    </div>
  );
}

export default ListSchedule;
